import { formatMoney } from './utils-format-money.js';

/**
 * @typedef {Object} Item
 * @property {number} id - The variant ID of the add-on.
 * @property {number} quantity - The quantity of the add-on.
 * @property {number} price - The price of the add-on in cents.
 * @property {number} compareAtPrice - The compare at price of the add-on in cents.
 */

/**
 * @class ProductAddOn
 * @extends HTMLElement
 * @description Handles the popular add on functionality.
 * @example <product-add-on class="block" data-product-id="{{ product.id }}" data-variant-id="{{ variant_id }}" data-compare-at-price="{{ variant_compare_price }}" data-price="{{ variant_price }}"></product-add-on>
 */
class ProductAddOn extends HTMLElement {
  input = this.querySelector('input[type="checkbox"]');
  controller = new AbortController();

  // typeIsPreselectedBefore = false;

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    this.input?.addEventListener('change', () => this.handleChange(), {
      signal: this.controller.signal,
    });

    this.unsubscribeVariantChange = subscribe(
      `variant:changed:${this.dataset.productId}:${this.sectionId}`,
      () => {
        console.log('product add on js variant changed');
        this.handleChange();
      }
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.unsubscribeVariantChange();
  }

  handleChange = () => {
    // Create item update object
    const item = {
      id: parseInt(this.dataset.variantId),
      quantity: 1,
      price: parseInt(this.dataset.price),
      compareAtPrice: parseInt(this.dataset.compareAtPrice) || parseInt(this.dataset.price),
    };

    // Based on the checkbox add or remove the popular add on
    this.input.checked ? this.addPopularAddOn(item) : this.removePopularAddOn(item);
  };

  /**
   * @method addPopularAddOn
   * @description Adds a popular add-on to the product.
   * @param {Item} item - The item to add.
   * @returns {void}
   */
  addPopularAddOn(item) {
    publish(`product-add-on:add:${this.dataset.productId}:${this.sectionId}`, item);
    this.classList.add('expanded');
  }

  removePopularAddOn(item) {
    publish(`product-add-on:remove:${this.dataset.productId}:${this.sectionId}`, item);
    this.classList.remove('expanded');
  }
}

customElements.define('product-add-on', ProductAddOn);

/**
 * @class ProductAddOnType
 * @extends HTMLElement
 * @description Changes the variant and price of the parent popular add on.
 */
class ProductAddOnType extends HTMLElement {
  popularAddOnParent = this.closest('product-add-on');
  controller = new AbortController();

  connectedCallback() {
    // The parent popular add on is required to be present
    if (!this.popularAddOnParent) return;
    this.addEventListener('change', (event) => this.handleChange(event), {
      signal: this.controller.signal,
    });
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  handleChange = (event) => {
    event.stopPropagation();

    // first remove the popular add on
    this.popularAddOnParent.removePopularAddOn({
      id: parseInt(this.popularAddOnParent.dataset.variantId),
      quantity: 1,
      price: parseInt(this.popularAddOnParent.dataset.price),
    });

    // Now set the new selected product variant and price on the parent popular add on for calculating the totals
    this.popularAddOnParent.setAttribute('data-variant-id', this.dataset.variantId);
    this.popularAddOnParent.setAttribute(
      'data-compare-at-price',
      this.dataset.compareAtPrice || this.dataset.price
    );
    this.popularAddOnParent.setAttribute('data-price', this.dataset.price);
    this.popularAddOnParent.classList.add('expanded');

    // Update the price of the parent popular add on
    const variantPrice = this.popularAddOnParent.querySelector('[data-current-price]');
    if (variantPrice) {
      this.popularAddOnParent.querySelector('[data-current-price]').textContent =
        formatMoney(this.dataset.price);
    }

    // Update the description of the parent popular add on based on selected item
    const description = this.popularAddOnParent.querySelector(
      '[data-product-add-on="description"]'
    );
    const descriptionSource = this.querySelector(
      '[data-product-add-on-type="description"]'
    );
    const newDescriptionText = descriptionSource?.textContent ?? '';
    if (description && newDescriptionText) {
      description.textContent = newDescriptionText;
    }

    // Set checked and dispatch change event to trigger the change handler
    const checkbox = this.popularAddOnParent.querySelector('input[type="checkbox"]');
    checkbox.checked = true;
    checkbox.dispatchEvent(new Event('change'));
  };
}

customElements.define('product-add-on-type', ProductAddOnType);
